import { Controller } from "@hotwired/stimulus"
import { syncChats, getCachedChats, getCachedMessages, getOutboxMessages, lastSyncedAt } from "../chat_offline_resources"

// Manages the chat sync page UI.
// Loads stats from IDB on connect, drives the Sync Now button,
// animates the 3 progress bars (chats / messages / cache), and shows the outbox badge.
export default class extends Controller {
  static targets = [
    "lastSyncedAt", "chatCount", "messageCount", "storageUsed",
    "syncButton",
    "progressSection",
    "chatsBar", "chatsBarLabel",
    "messagesBar", "messagesBarLabel",
    "cacheBar", "cacheBarLabel",
    "outboxSection", "outboxCount"
  ]

  async connect() {
    await this._loadStats()
    await this._checkOutbox()
  }

  async syncNow() {
    this._setSyncing(true)
    this._showProgressSection()
    this._setBar(this.chatsBarTarget,    this.chatsBarLabelTarget,    0,   "Syncing...")
    this._setBar(this.messagesBarTarget, this.messagesBarLabelTarget, 0,   "—")
    this._setBar(this.cacheBarTarget,    this.cacheBarLabelTarget,    0,   "—")

    try {
      const { chats, messages } = await syncChats()

      // Conversations done
      this._setBar(this.chatsBarTarget, this.chatsBarLabelTarget, 100, `${chats.length}`)

      // Total messages across all chats
      let totalMessages = 0
      Object.values(messages).forEach(msgs => { totalMessages += msgs.length })
      this._setBar(this.messagesBarTarget, this.messagesBarLabelTarget, 100, `${totalMessages}`)

      // Storage / cache bar
      await this._updateCacheBar()

      await this._loadStats()
      await this._checkOutbox()
    } catch (err) {
      this._setBar(this.chatsBarTarget,    this.chatsBarLabelTarget,    0, "Failed")
      this._setBar(this.messagesBarTarget, this.messagesBarLabelTarget, 0, "—")
    } finally {
      this._setSyncing(false)
    }
  }

  // -- private --

  async _loadStats() {
    // Last synced timestamp
    if (this.hasLastSyncedAtTarget) {
      const ts = lastSyncedAt()
      if (ts) {
        const d = new Date(ts)
        this.lastSyncedAtTarget.textContent = d.toLocaleString("en-AU", {
          day: "numeric", month: "short", hour: "numeric", minute: "2-digit"
        })
      } else {
        this.lastSyncedAtTarget.textContent = "Never"
      }
    }

    // Chat and message counts from IDB
    try {
      const chats = await getCachedChats()
      if (this.hasChatCountTarget) this.chatCountTarget.textContent = chats.length

      let msgTotal = 0
      for (const chat of chats) {
        const msgs = await getCachedMessages(chat.id)
        msgTotal += msgs.length
      }
      if (this.hasMessageCountTarget) this.messageCountTarget.textContent = msgTotal
    } catch {
      // IDB not available
    }

    // Storage estimate
    if (this.hasStorageUsedTarget && navigator.storage?.estimate) {
      try {
        const estimate = await navigator.storage.estimate()
        const mb = ((estimate.usage || 0) / 1024 / 1024).toFixed(1)
        this.storageUsedTarget.textContent = `${mb} MB`
      } catch {
        this.storageUsedTarget.textContent = "Unknown"
      }
    }
  }

  async _checkOutbox() {
    if (!this.hasOutboxSectionTarget) return
    try {
      const pending = await getOutboxMessages()
      if (pending.length > 0) {
        this.outboxSectionTarget.classList.remove("hidden")
        if (this.hasOutboxCountTarget) this.outboxCountTarget.textContent = pending.length
      } else {
        this.outboxSectionTarget.classList.add("hidden")
      }
    } catch {
      // IDB not available
    }
  }

  _showProgressSection() {
    if (this.hasProgressSectionTarget) {
      this.progressSectionTarget.classList.remove("hidden")
    }
  }

  _setBar(barEl, labelEl, percent, label) {
    if (barEl)   barEl.style.width = `${percent}%`
    if (labelEl) labelEl.textContent = label
  }

  async _updateCacheBar() {
    if (!this.hasCacheBarTarget || !navigator.storage?.estimate) return
    try {
      const estimate = await navigator.storage.estimate()
      const usage  = estimate.usage || 0
      const quota  = estimate.quota || 1
      const pct    = Math.min(Math.round((usage / quota) * 100), 100)
      const mb     = (usage / 1024 / 1024).toFixed(1)
      this._setBar(this.cacheBarTarget, this.cacheBarLabelTarget, pct, `${mb} MB`)
    } catch {
      // storage estimate not available
    }
  }

  _setSyncing(syncing) {
    if (!this.hasSyncButtonTarget) return
    this.syncButtonTarget.disabled = syncing
    this.syncButtonTarget.textContent = syncing ? "Syncing..." : "Sync Now"
  }
}
